import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown, Search, Filter as FilterIcon, ArrowUpRightFromSquare } from 'lucide-react';
import { Pagination } from '@/components/navigation/Pagination';
import {
  type IngestionFilter,
  type DateRangeInput,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
} from '@/lib/api/reporting/metrics';
import { MetricBreakdown } from '@/lib/api/types.gen';
import { Link } from 'react-router-dom';
import { routes } from '@/lib/routing/route-paths';

interface IngestionCameraHealthProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters: IngestionFilter;
  onSelectCamera?: (vpcId: string, displayName?: string) => void;
}

interface CameraRow {
  id: string;
  name: string;
  deviceName: string;
  v1DeviceName: string;
  deviceBrand: string;
  siteName: string;
  siteId: string;
  customerName: string;
  customerId: string;
  eventCount: number;
  successCount: number;
  failureCount: number;
  filteredCount: number;
}

type SortField = 'name' | 'siteName' | 'eventCount' | 'successCount' | 'filteredCount' | 'failureCount';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;

export function IngestionCameraHealth({ tenantId, dateRange, filters, onSelectCamera }: IngestionCameraHealthProps) {
  const [sortField, setSortField] = useState<SortField>('eventCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const dateStart = new Date(dateRange.dateStart);
  const dateEnd = new Date(dateRange.dateEnd);

  const { data: eventData, isLoading: eventsLoading, error } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: MetricBreakdown.Camera,
      dateStart,
      dateEnd,
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const { data: successData, isLoading: successLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.SuccessCount,
      breakdown: MetricBreakdown.Camera,
      dateStart,
      dateEnd,
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const { data: failureData, isLoading: failureLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.FailureCount,
      breakdown: MetricBreakdown.Camera,
      dateStart,
      dateEnd,
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const isLoading = eventsLoading || successLoading || failureLoading;

  const cameras = useMemo(() => {
    if (!eventData?.data) return [];

    const successMap = new Map<string, number>();
    if (successData?.data) {
      for (const point of successData.data) {
        const id = point.attributes?.['id'] ?? '';
        successMap.set(id, point.value ?? 0);
      }
    }

    const failureMap = new Map<string, number>();
    if (failureData?.data) {
      for (const point of failureData.data) {
        const id = point.attributes?.['id'] ?? '';
        failureMap.set(id, point.value ?? 0);
      }
    }

    const rows: CameraRow[] = eventData.data.map((point) => {
      const id = point.attributes?.['id'] ?? '';
      const eventCount = point.value ?? 0;
      const successCount = successMap.get(id) ?? 0;
      const failureCount = failureMap.get(id) ?? 0;
      const filteredCount = Math.max(0, eventCount - successCount - failureCount);

      return {
        id,
        name: point.attributes?.['name'] ?? '',
        deviceName: point.attributes?.['deviceName'] ?? '',
        v1DeviceName: point.attributes?.['v1DeviceName'] ?? '',
        deviceBrand: point.attributes?.['deviceBrand'] ?? '',
        siteName: point.attributes?.['siteName'] ?? '',
        siteId: point.attributes?.['siteId'] ?? point.attributes?.['projectId'] ?? '',
        customerName: point.attributes?.['customerName'] ?? '',
        customerId: point.attributes?.['customerId'] ?? point.attributes?.['companyId'] ?? '',
        eventCount,
        successCount,
        failureCount,
        filteredCount,
      };
    });

    const needle = search.toLowerCase();
    const filtered = needle
      ? rows.filter((cam) => {
          const display = cam.deviceName || cam.v1DeviceName || cam.name || cam.id;
          return (
            display.toLowerCase().includes(needle) ||
            cam.siteName.toLowerCase().includes(needle) ||
            cam.customerName.toLowerCase().includes(needle) ||
            cam.id.toLowerCase().includes(needle)
          );
        })
      : rows;

    return filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const cmp = typeof aVal === 'string'
        ? (aVal as string).localeCompare(bVal as string)
        : (aVal as number) - (bVal as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [eventData, successData, failureData, sortField, sortDir, search]);

  const totalPages = Math.max(1, Math.ceil(cameras.length / PAGE_SIZE));
  const paginatedCameras = cameras.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-white/20" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-white/50" />
      : <ArrowDown className="h-3 w-3 text-white/50" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400">
        Failed to load camera data: {error.message}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#2E3835] bg-[#1a1a1a] overflow-hidden">
      <div className="px-4 py-3 border-b border-[#2E3835] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-white/80">Camera Health</h3>
          <p className="text-xs text-white/30 mt-0.5">{cameras.length} cameras with activity</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-white/25" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search cameras..."
            className="h-7 w-[200px] rounded-md border border-[#2E3835] bg-transparent pl-7 pr-2 text-xs text-white/60 placeholder:text-white/25 outline-none focus:border-white/20"
          />
        </div>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[#2E3835] bg-white/[0.02] text-[10px] uppercase tracking-wider text-white/30">
            <th className="text-left px-3 py-2 font-medium">
              <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1">
                Device <SortIcon field="name" />
              </button>
            </th>
            <th className="text-left px-3 py-2 font-medium">
              <button onClick={() => toggleSort('siteName')} className="inline-flex items-center gap-1">
                Site <SortIcon field="siteName" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <button onClick={() => toggleSort('eventCount')} className="inline-flex items-center gap-1">
                Events <SortIcon field="eventCount" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <span className="text-emerald-400/60">Success</span>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <button onClick={() => toggleSort('filteredCount')} className="inline-flex items-center gap-1">
                <span className="text-amber-400/60">Filtered</span> <SortIcon field="filteredCount" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <button onClick={() => toggleSort('failureCount')} className="inline-flex items-center gap-1">
                <span className="text-red-400/60">Failed</span> <SortIcon field="failureCount" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium w-[80px]"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.03]">
          {paginatedCameras.map((cam) => {
            const displayName = cam.deviceName || cam.v1DeviceName || cam.name || cam.id.slice(0, 8);
            return (
              <tr
                key={cam.id}
                className="hover:bg-white/[0.02] transition-colors"
              >
                <td className="px-3 py-2">
                  <Link
                    to={`/ingestion?tab=pipeline&videoProcessorConfigId=${cam.id}`}
                    className="block truncate max-w-[200px] text-blue-400/70 hover:text-blue-400 hover:underline font-medium"
                    title={displayName}
                  >
                    {displayName}
                  </Link>
                  <div className="text-white/20 font-mono text-[10px]" title={cam.id}>
                    {cam.id.slice(0, 8)}
                    {cam.deviceBrand && <span className="ml-1.5 font-sans">{cam.deviceBrand}</span>}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {cam.siteName && cam.siteId && cam.customerId ? (
                    <Link
                      to={routes.customerSite({ customerId: cam.customerId, siteId: cam.siteId })}
                      target="_blank"
                      className="inline-flex items-center gap-1 truncate max-w-[160px] text-blue-400/70 hover:text-blue-400 hover:underline"
                      title={cam.siteName}
                    >
                      {cam.siteName}
                      <ArrowUpRightFromSquare className="h-2.5 w-2.5 shrink-0" />
                    </Link>
                  ) : (
                    <div className="text-white/60 truncate max-w-[160px]" title={cam.siteName}>
                      {cam.siteName || '--'}
                    </div>
                  )}
                  {cam.customerName && cam.customerId ? (
                    <Link
                      to={routes.customer({ customerId: cam.customerId })}
                      target="_blank"
                      className="inline-flex items-center gap-0.5 text-[10px] text-blue-400/40 hover:text-blue-400/70 hover:underline truncate"
                      title={cam.customerName}
                    >
                      {cam.customerName}
                      <ArrowUpRightFromSquare className="h-2 w-2 shrink-0" />
                    </Link>
                  ) : cam.customerName ? (
                    <div className="text-white/25 text-[10px]">{cam.customerName}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right font-mono text-white/60">
                  {cam.eventCount.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-emerald-400/70">
                  {cam.successCount.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-amber-400/70">
                  {cam.filteredCount.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono text-red-400/70">
                  {cam.failureCount > 0 ? cam.failureCount.toLocaleString() : <span className="text-white/15">0</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onSelectCamera?.(cam.id, displayName)}
                    title="Filter ingestion logs to this camera"
                    className="rounded p-1 text-white/30 hover:text-white/70 hover:bg-white/[0.05] border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <FilterIcon className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {cameras.length === 0 && (
        <div className="py-12 text-center text-xs text-white/30">
          {search ? 'No cameras match your search' : 'No camera data available for this period'}
        </div>
      )}

      {totalPages > 1 && (
        <div className="border-t border-[#2E3835]">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

